<template>
  <div class="so-product-selector">
    <!-- Inline mode: render the product table directly -->
    <template v-if="inline">
      <data-table
        :config="tableConfig"
        :data="productRows"
        @button-click="handleProductSelect"
      />
    </template>
    <!-- Default mode: button that opens a modal -->
    <template v-else>
      <Button
        data-testid="so-product-add-btn"
        :label="t('Adicionar Produto')"
        icon="pi pi-plus"
        size="small"
        severity="info"
        @click="openProductModal"
      />
    </template>

    <!-- Selected products list (always shown below the table/button) -->
    <div class="selected-products" v-if="selectedList.length > 0">
      <div v-for="item in selectedList" :key="item.product.so_stock_product_id" class="selected-product-row">
        <span class="product-name">{{ item.product.so_product_name }}</span>
        <span class="product-price">{{ n(item.product.so_stock_effective_price ?? 0, 'currency') }}</span>
        <div class="qty-controls">
          <Button :data-testid="`so-product-decrement-${item.product.so_stock_product_id}-btn`" icon="pi pi-minus" severity="secondary" text size="small" @click="decrementQty(item.product.so_stock_product_id)" />
          <span class="qty-value">{{ item.qty }}</span>
          <Button :data-testid="`so-product-increment-${item.product.so_stock_product_id}-btn`" icon="pi pi-plus" severity="secondary" text size="small" @click="incrementQty(item.product.so_stock_product_id)" />
        </div>
        <span class="product-subtotal">{{ n((item.product.so_stock_effective_price ?? 0) * item.qty, 'currency') }}</span>
        <Button
          :data-testid="`so-product-remove-${item.product.so_stock_product_id}-btn`"
          icon="pi pi-trash"
          severity="danger"
          text
          size="small"
          @click="removeProduct(item.product.so_stock_product_id)"
        />
      </div>
      <div class="products-total" v-if="showTotal">
        {{ t('Total') }}: {{ n(total, 'currency') }}
      </div>
    </div>
    <div v-else class="no-products">
      {{ t('Nenhum produto adicionado') }}
    </div>

    <!-- Product selection modal (only used in non-inline mode) -->
    <modal
      v-if="!inline"
      elem-id="so-product-selector-modal"
      :show="showModal"
      :title="t('Selecionar Produto')"
      size="large"
      :close-label="t('Fechar')"
      :show-save="false"
      @close="closeProductModal"
      @dismiss="closeProductModal"
    >
      <template v-if="showModal">
        <data-table
          :config="tableConfig"
          :data="productRows"
          @button-click="handleProductSelect"
        />
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { useSOStock } from '../../../composables/useSOStock';
import { log } from '../../../utils/DebugLogger';
import { imageColumn, franchiseColumn, priceColumn } from '../../../grid/productTableColumns';

type Id = number | string;

type ProductSelection = Record<string, number>;

interface MatrixRow {
  so_stock_product_id: Id;
  so_stock_franchise_name: string;
  so_stock_quantity: number;
  so_stock_effective_price: number;
  so_product_name: string;
  so_product_category_name?: string;
  so_product_track_stock?: number | string;
  so_product_primary_image_thumbnail_url?: string;
  [key: string]: unknown;
}

interface Props {
  modelValue?: ProductSelection;
  products?: MatrixRow[] | null;
  showTotal?: boolean;
  franchiseId?: Id | null;
  inline?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  products: null,
  showTotal: true,
  franchiseId: null
});

const emit = defineEmits<{
  'update:modelValue': [value: ProductSelection];
  'total-change': [total: number];
}>();

const { t, n } = useI18n();

const { fetchStock } = useSOStock();
const internalProducts = ref<MatrixRow[]>([]);

const productsList = computed<MatrixRow[]>(() => props.products || internalProducts.value);

const selectedProducts = ref<ProductSelection>({ ...props.modelValue });

const isSameSelection = (a: ProductSelection, b: ProductSelection): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(k => a[k] === b[k]);
};

watch(() => props.modelValue, (newVal) => {
  if (!isSameSelection(newVal, selectedProducts.value)) {
    selectedProducts.value = { ...newVal };
  }
}, { deep: true });

watch(selectedProducts, (newVal) => {
  if (!isSameSelection(newVal, props.modelValue)) {
    emit('update:modelValue', { ...newVal });
  }
}, { deep: true });

interface SelectedItem {
  product: MatrixRow;
  qty: number;
}

const selectedList = computed<SelectedItem[]>(() => {
  const entries = Object.entries(selectedProducts.value);
  return entries
    .filter(([, qty]) => qty > 0)
    .map(([prodId, qty]) => {
      const product = productsList.value.find(p => String(p.so_stock_product_id) === String(prodId));
      return { product, qty };
    })
    .filter((item): item is SelectedItem => item.product !== undefined);
});

const total = computed<number>(() => {
  let sum = 0;
  for (const [prodId, qty] of Object.entries(selectedProducts.value)) {
    if (qty > 0) {
      const prod = productsList.value.find(p => String(p.so_stock_product_id) === String(prodId));
      if (prod) sum += (prod.so_stock_effective_price || 0) * qty;
    }
  }
  return sum;
});

watch(() => total.value, (newTotal) => {
  emit('total-change', newTotal);
});

const getProductQty = (prodId: Id): number => selectedProducts.value[String(prodId)] || 0;

interface ProductRow extends MatrixRow {
  so_stock_remaining_qty: number | null;
}

// Stock still addable for a tracked product = on-hand minus the qty already
// picked. null = no cap (untracked product → always selectable).
const remainingStock = (prod: MatrixRow): number | null => {
  if (Number(prod.so_product_track_stock) !== 1) return null;
  return Number(prod.so_stock_quantity) - getProductQty(prod.so_stock_product_id);
};

// Picker rows carry the live remaining-stock count so the table re-renders
// (and the add button disables) as items are added.
const productRows = computed<ProductRow[]>(() =>
  productsList.value.map(prod => ({
    ...prod,
    so_stock_remaining_qty: remainingStock(prod)
  }))
);

const rowAtStockLimit = (row: Record<string, unknown>): boolean => {
  const qty = row.so_stock_remaining_qty;
  return typeof qty === 'number' && qty <= 0;
};

const showModal = ref<boolean>(false);

const tableConfig = computed(() => ({
  columns: [
    imageColumn(t),
    { data: 'so_product_name', title: t('Produto'), sortable: true, filterType: 'text',
      mergedColumns: [
        { data: 'so_product_category_name', title: t('Categoria') },
        franchiseColumn(t, 'so_stock_franchise_name', true)
      ] },
    priceColumn(t, 'so_stock_effective_price', t('Preço'), 120),
    {
      data: 'so_stock_remaining_qty',
      title: t('Quantidade disponível'),
      width: 150,
      sortable: true,
      cellRenderer: ({ value }: { value: unknown }): string => {
        if (value === null || value === undefined) return '—';
        return String(value);
      }
    },
    {
      data: null, title: '', width: 110, sortable: false,
      renderType: 'action_buttons',
      buttons: [
        {
          text: t('Adicionar'),
          icon: 'pi pi-plus',
          action: 'add',
          testId: 'so-product-add-row-btn',
          disabled: (row: Record<string, unknown>): boolean => rowAtStockLimit(row),
          tooltip: (row: Record<string, unknown>): string =>
            rowAtStockLimit(row) ? t('Sem estoque') : ''
        }
      ]
    }
  ],
  pagination: true,
  pageLength: 15,
  height: '350px',
  heightMode: 'fixed',
  size: 'small',
  stripedRows: true,
  showFilters: true,
  filterDisplay: 'row'
}));

const openProductModal = (): void => {
  showModal.value = true;
};

const closeProductModal = (): void => {
  showModal.value = false;
};

interface ProductSelectEvent {
  action?: string;
  data?: MatrixRow;
}

const handleProductSelect = (event: ProductSelectEvent): void => {
  if (event.action === 'add' && event.data?.so_stock_product_id) {
    const prodId = String(event.data.so_stock_product_id);
    const currentQty = selectedProducts.value[prodId] || 0;
    const newSelection: ProductSelection = {
      ...selectedProducts.value,
      [prodId]: currentQty + 1
    };
    selectedProducts.value = newSelection;
    eventBus.emit('globalMessage.success', t('Produto adicionado'));
  }
};

const removeProduct = (prodId: Id): void => {
  const copy = { ...selectedProducts.value };
  delete copy[String(prodId)];
  selectedProducts.value = copy;
};

const incrementQty = (prodId: Id): void => {
  const key = String(prodId);
  const current = selectedProducts.value[key] || 0;
  if (current < 99) {
    selectedProducts.value = { ...selectedProducts.value, [key]: current + 1 };
  }
};

const decrementQty = (prodId: Id): void => {
  const key = String(prodId);
  const current = selectedProducts.value[key] || 0;
  if (current <= 1) {
    removeProduct(prodId);
  } else {
    selectedProducts.value = { ...selectedProducts.value, [key]: current - 1 };
  }
};

const loadProducts = async (): Promise<void> => {
  if (!props.products) {
    try {
      const fid = props.franchiseId ? Number(props.franchiseId) : undefined;
      const rows = (await fetchStock(fid)) as unknown as MatrixRow[];
      internalProducts.value = rows;
    } catch (err) {
      log('SOProductSelector', 'loadProducts', 'Error loading products', err);
    }
  }
};

onMounted(() => {
  loadProducts();
});

defineExpose({
  total,
  selectedList,
  reset: (): void => { selectedProducts.value = {}; }
});
</script>

<style scoped>
.so-product-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-products {
  margin-top: 12px;
}

.selected-product-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--p-surface-100);
}

.selected-product-row .product-name {
  flex: 1;
  font-weight: 500;
}

.selected-product-row .product-price {
  color: var(--p-text-muted-color);
  min-width: 70px;
  text-align: right;
}

.selected-product-row .product-subtotal {
  font-weight: 600;
  min-width: 80px;
  text-align: right;
}

.qty-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.qty-controls .qty-value {
  min-width: 20px;
  text-align: center;
  font-weight: 600;
}

.products-total {
  text-align: right;
  font-weight: 600;
  padding-top: 8px;
  margin-top: 4px;
}

.no-products {
  text-align: center;
  padding: 20px;
  color: var(--p-text-muted-color);
}
</style>
